/** Public feed deliberately contains only score summaries, never scorer app_state. */
export interface EventLiveGame {
  game_number: number;
  player_1_score: number;
  player_2_score: number;
  /**
   * Additive 2026-10-25, stored (final) games only: `'in_progress'` marks a retired
   * match's unfinished game. Absent on live games and older publications.
   */
  status?: string;
}
export interface EventLiveScore {
  schedule_id: string;
  source_revision: number;
  match_id: string | null;
  draw_id: string | null;
  draw_match_id: string | null;
  status: string;
  winner_id: string | null;
  is_walkover: boolean;
  outcome: string | null;
  player_1_games: number | null;
  player_2_games: number | null;
  games: EventLiveGame[];
  current_game: EventLiveGame | null;
  /** Additive 2026-10-25: the player who retired; absent on older publications. */
  retired_player_id?: string | null;
}
export interface EventLiveScoresPayload {
  schema_version: 1;
  snapshot_required?: boolean;
  matches: EventLiveScore[];
  removed_schedule_ids: string[];
  removed_source_revisions: Record<string, number>;
}
export interface EventLiveScoresEnvelope {
  event_id: string;
  revision: number;
  topology_revision: number;
  as_of: string;
  snapshot?: boolean;
  payload: EventLiveScoresPayload;
}
export type EventLiveScoresSnapshot = EventLiveScoresEnvelope & { snapshot: true };
export interface LiveScoresState {
  byScheduleId: Record<string, EventLiveScore>;
  sourceRevisions: Record<string, number>;
  revision: number;
  topologyRevision: number;
  updatedAt: string | null;
}
export const emptyLiveScoresState = (): LiveScoresState => ({
  byScheduleId: {},
  sourceRevisions: {},
  revision: 0,
  topologyRevision: 0,
  updatedAt: null,
});
const object = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
const counter = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const finiteScore = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);
const nullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';
const nullableCounter = (value: unknown): value is number | null =>
  value === null || counter(value);
function game(value: unknown): EventLiveGame | null {
  const g = object(value);
  if (
    !g ||
    !counter(g.game_number) ||
    g.game_number < 1 ||
    !finiteScore(g.player_1_score) ||
    !finiteScore(g.player_2_score)
  )
    return null;
  return {
    game_number: g.game_number,
    player_1_score: g.player_1_score,
    player_2_score: g.player_2_score,
    // Optional and additive: kept when well-formed, dropped (never fatal) otherwise.
    ...(typeof g.status === 'string' ? { status: g.status } : {}),
  };
}
function score(value: unknown): EventLiveScore | null {
  const s = object(value);
  if (
    !s ||
    typeof s.schedule_id !== 'string' ||
    !s.schedule_id ||
    !counter(s.source_revision) ||
    !nullableString(s.match_id) ||
    !nullableString(s.draw_id) ||
    !nullableString(s.draw_match_id) ||
    typeof s.status !== 'string' ||
    !nullableString(s.winner_id) ||
    typeof s.is_walkover !== 'boolean' ||
    !nullableString(s.outcome) ||
    !nullableCounter(s.player_1_games) ||
    !nullableCounter(s.player_2_games) ||
    !Array.isArray(s.games)
  )
    return null;
  const games: EventLiveGame[] = [];
  for (const value of s.games) {
    const parsed = game(value);
    if (!parsed) return null;
    games.push(parsed);
  }
  const current = s.current_game === null ? null : game(s.current_game);
  if (s.current_game !== null && !current) return null;
  return {
    schedule_id: s.schedule_id,
    source_revision: s.source_revision,
    match_id: s.match_id,
    draw_id: s.draw_id,
    draw_match_id: s.draw_match_id,
    status: s.status,
    winner_id: s.winner_id,
    is_walkover: s.is_walkover,
    outcome: s.outcome,
    player_1_games: s.player_1_games,
    player_2_games: s.player_2_games,
    games,
    current_game: current,
    ...(nullableString(s.retired_player_id) ? { retired_player_id: s.retired_player_id } : {}),
  };
}
export function parseEventLiveScoresEnvelope(
  value: unknown,
  eventId: string,
): EventLiveScoresEnvelope | null {
  const row = object(value);
  const payload = object(row?.payload);
  if (
    !row ||
    row.event_id !== eventId ||
    !counter(row.revision) ||
    !counter(row.topology_revision) ||
    typeof row.as_of !== 'string' ||
    !Number.isFinite(Date.parse(row.as_of)) ||
    !payload ||
    payload.schema_version !== 1 ||
    !Array.isArray(payload.matches) ||
    !Array.isArray(payload.removed_schedule_ids)
  )
    return null;
  const matches: EventLiveScore[] = [];
  for (const value of payload.matches) {
    const parsed = score(value);
    if (!parsed) return null;
    matches.push(parsed);
  }
  const versions = object(payload.removed_source_revisions);
  if (!versions) return null;
  const removed: string[] = [];
  const removedVersions: Record<string, number> = {};
  for (const id of payload.removed_schedule_ids) {
    if (typeof id !== 'string' || !id || !counter(versions[id])) return null;
    removed.push(id);
    removedVersions[id] = versions[id];
  }
  return {
    event_id: eventId,
    revision: row.revision,
    topology_revision: row.topology_revision,
    as_of: row.as_of,
    ...(row.snapshot === true ? { snapshot: true } : {}),
    payload: {
      schema_version: 1,
      ...(payload.snapshot_required === true ? { snapshot_required: true } : {}),
      matches,
      removed_schedule_ids: removed,
      removed_source_revisions: removedVersions,
    },
  };
}
/** Source counters protect rows/tombstones independently of the batched event revision. */
export function mergeEventLiveScores(
  state: LiveScoresState,
  envelope: EventLiveScoresEnvelope,
): LiveScoresState {
  if (!envelope.snapshot && envelope.revision <= state.revision) return state;
  const byScheduleId = { ...state.byScheduleId };
  const sourceRevisions = { ...state.sourceRevisions };
  for (const row of envelope.payload.matches) {
    if (row.source_revision <= (sourceRevisions[row.schedule_id] ?? -1)) continue;
    sourceRevisions[row.schedule_id] = row.source_revision;
    byScheduleId[row.schedule_id] = row;
  }
  for (const id of envelope.payload.removed_schedule_ids) {
    const version = envelope.payload.removed_source_revisions[id];
    if (version <= (sourceRevisions[id] ?? -1)) continue;
    sourceRevisions[id] = version;
    delete byScheduleId[id];
  }
  return {
    byScheduleId,
    sourceRevisions,
    revision: Math.max(state.revision, envelope.revision),
    topologyRevision: Math.max(state.topologyRevision, envelope.topology_revision),
    updatedAt:
      state.updatedAt && Date.parse(state.updatedAt) > Date.parse(envelope.as_of)
        ? state.updatedAt
        : envelope.as_of,
  };
}
